import React, { useState, useEffect } from "react";
//import { useState, useEffect } from "react"
import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import {
  Star,
  Heart,
  ShoppingBag,
  ArrowLeft,
  Plus,
  Minus,
  ImageOff,
  ZoomIn,
} from "lucide-react";
import { products } from "../data/products";
import { useCart } from "../context/CartContext";

// Componente para el placeholder de imagen
const NoImagePlaceholder = ({ size = "w-12 h-12" }) => (
  <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 text-gray-400">
    <ImageOff className={size} />
    <span className="text-xs mt-1">No Image</span>
  </div>
);

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);


  // 👇 Scroll siempre al inicio cuando se carga el componente
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // --- Estados para el ZOOM ---
  const [zoomStyle, setZoomStyle] = useState({
    transformOrigin: "center center",
    transform: "scale(1)",
  });
  const [isZoomed, setIsZoomed] = useState(false);
  // --------------------------

  const product = products.find((p) => p.id === parseInt(id));

  // Estados para manejo de errores de imagen
  const [mainImageError, setMainImageError] = useState(false);
  const [thumbnailErrors, setThumbnailErrors] = useState(
    product ? new Array(product.images.length).fill(false) : []
  );

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Producto no encontrado
          </h1>
          <button
            onClick={() => navigate("/products")}
            className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors"
          >
            Ver Productos
          </button>
        </div>
      </div>
    );
  }

  // --- Funciones de ZOOM ---
  const handleZoom = (e) => {
    if (mainImageError) return;

    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();

    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;

    setZoomStyle({
      transformOrigin: `${x}% ${y}%`,
      transform: "scale(2.5)",
      transition: "transform 0.05s ease-out",
    });
    setIsZoomed(true);
  };

  const handleMouseLeave = () => {
    setZoomStyle({
      transformOrigin: "center center",
      transform: "scale(1)",
      transition: "transform 0.2s ease-out",
    });
    setIsZoomed(false);
  };
  // --------------------------

  const handleThumbnailError = (index) => {
    setThumbnailErrors((prevErrors) => {
      const newErrors = [...prevErrors];
      newErrors[index] = true;
      return newErrors;
    });
  };

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8 transition-colors"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ x: -5 }}
        >
          <ArrowLeft className="w-5 h-5" />
          Volver
        </motion.button>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Columna imágenes */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            {/* Imagen Principal y Zoom */}
            <div
              className={`aspect-square overflow-hidden rounded-3xl bg-white shadow-xl relative ${!mainImageError ? "cursor-crosshair" : ""
                } ${isZoomed
                  ? "z-20 shadow-2xl ring-4 ring-blue-200"
                  : "z-10"
                }`}
              onMouseMove={handleZoom}
              onMouseLeave={handleMouseLeave}
            >
              {mainImageError ? (
                <NoImagePlaceholder size="w-24 h-24" />
              ) : (
                <>
                  <motion.img
                    key={selectedImage}
                    src={product.images[selectedImage]}
                    alt={product.name}
                    style={zoomStyle}
                    className="w-full h-full object-cover"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    onError={() => setMainImageError(true)}
                  />
                  {/* Indicador visual del zoom */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1"
                  >
                    <ZoomIn className="w-4 h-4" />
                    <span className="hidden sm:block">Mover para zoom</span>
                  </motion.div>
                </>
              )}
            </div>

            {/* Miniaturas */}
            <div className="grid grid-cols-4 gap-3">
              {product.images.map((image, index) => (
                <motion.button
                  key={index}
                  onClick={() => {
                    setSelectedImage(index);
                    if (!thumbnailErrors[index]) {
                      setMainImageError(false);
                      handleMouseLeave();
                    }
                  }}
                  className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${selectedImage === index
                      ? "border-blue-500 ring-2 ring-blue-200"
                      : "border-gray-200 hover:border-gray-300"
                    }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {thumbnailErrors[index] ? (
                    <NoImagePlaceholder size="w-8 h-8" />
                  ) : (
                    <img
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={() => handleThumbnailError(index)}
                    />
                  )}
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Columna info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            {/* Rating */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${i < Math.floor(product.rating)
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-gray-300"
                        }`}
                    />
                  ))}
                </div>
                <span className="text-sm font-medium text-gray-600">
                  {product.rating} ({product.reviews} reseñas)
                </span>
              </div>

              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {product.name}
              </h1>

              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {product.description}
              </p>

              <div className="flex items-center gap-4 mb-6">
                <span className="text-4xl font-bold text-gray-900">
                  ${product.price}
                </span>
                {product.originalPrice > product.price && (
                  <span className="text-2xl text-gray-500 line-through">
                    ${product.originalPrice}
                  </span>
                )}
                {product.originalPrice > product.price && (
                  <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-semibold">
                    Ahorra ${product.originalPrice - product.price}
                  </span>
                )}
              </div>
            </div>

            {/* Cantidad + Botones */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cantidad
                </label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-xl font-semibold w-12 text-center">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex gap-4">
                <motion.button
                  onClick={handleAddToCart}
                  disabled={!product.inStock}
                  className={`flex-1 flex items-center justify-center gap-3 px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-200 ${product.inStock
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl"
                      : "bg-gray-200 text-gray-500 cursor-not-allowed"
                    }`}
                  whileHover={product.inStock ? { scale: 1.02, y: -2 } : {}}
                  whileTap={product.inStock ? { scale: 0.98 } : {}}
                >
                  <ShoppingBag className="w-5 h-5" />
                  {product.inStock ? "Agregar al Carrito" : "Agotado"}
                </motion.button>

                <motion.button
                  className="p-4 border-2 border-gray-200 rounded-2xl hover:border-red-300 hover:bg-red-50 transition-all duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Heart className="w-6 h-6 text-gray-600 hover:text-red-500 transition-colors" />
                </motion.button>
              </div>

              {!product.inStock && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <p className="text-yellow-800 font-medium">
                    Este producto está temporalmente agotado. Te notificaremos
                    cuando esté disponible.
                  </p>
                </div>
              )}
            </div>

            {/* Información de envío */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-4">
                Información de Envío
              </h3>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Envío estándar:</span>
                  <span>Gratis en compras +$50</span>
                </div>
                <div className="flex justify-between">
                  <span>Envío express:</span>
                  <span>$9.99 (24-48 horas)</span>
                </div>
                <div className="flex justify-between">
                  <span>Devoluciones:</span>
                  <span>30 días gratis</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
